import React from 'react'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts'
import { PortfolioGraphData, AllPortfoliosGraphData } from '@/types/portfolio'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/utils'

interface PortfolioChartProps {
  graphData: PortfolioGraphData | AllPortfoliosGraphData | null
  loading?: boolean
  mobile?: boolean
  darkMode?: boolean
}

const formatMoney = (value: number) => {
  if (value === null || value === undefined) {
    return '$0'
  }

  const absValue = Math.abs(value)

  if (absValue >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`
  } else if (absValue >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`
  } else if (absValue >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`
  } else if (absValue >= 1) {
    return `$${value.toLocaleString('en-US')}`
  }

  return `$${value.toFixed(0)}`
}

const formatYAxisMoney = (value: number) => {
  if (value === 0) return '0'
  if (value >= 1000000000) {
    return `$ ${Math.round(value / 1000000000)}B`
  } else if (value >= 1000000) {
    return `$ ${Math.round(value / 1000000)}M`
  } else if (value >= 1000) {
    return `$ ${Math.round(value / 1000)}K`
  }
  return `$ ${Math.round(value)}`
}

const PortfolioChart: React.FC<PortfolioChartProps> = ({
  graphData,
  loading = false,
  mobile = false,
  darkMode = false,
}) => {
  const tickColor = darkMode ? 'rgba(253,253,253,0.64)' : 'rgba(45,41,35,0.64)'
  const gridColor = darkMode ? 'rgba(253,253,253,0.08)' : 'rgba(45,41,35,0.12)'
  const axisStroke = darkMode ? 'rgba(253,253,253,0.18)' : 'rgba(45,41,35,0.12)'
  const chartHeight = mobile ? 431 : 465
  const plotHeight = mobile ? 352 : 388
  const cardClass = cn(
    'relative rounded-none shadow-none',
    mobile
      ? darkMode
        ? 'border border-[#171717] bg-[rgba(23,23,23,0.72)]'
        : 'border border-white bg-[rgba(255,255,255,0.72)]'
      : darkMode
        ? 'border border-[#171717] bg-[rgba(23,23,23,0.72)]'
        : 'border border-[rgba(45,41,35,0.08)] bg-white'
  )

  if (loading) {
    return (
      <Card variant="default" padding="none" className={cardClass}>
        <CardContent className="pt-0">
          <div className="flex w-full items-center justify-center px-6" style={{ height: `${chartHeight}px` }}>
            <div className="w-full space-y-4">
              <Skeleton className="h-[300px] w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!graphData || !graphData.monthly_values || graphData.monthly_values.length === 0) {
    return (
      <Card variant="default" padding="none" className={cardClass}>
        <CardContent className="pt-0">
          <div className="flex w-full items-center justify-center px-6" style={{ height: `${chartHeight}px` }}>
            <p className={cn(darkMode ? 'text-[rgba(253,253,253,0.64)]' : 'text-[rgba(45,41,35,0.64)]')}>
              No chart data available
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const chartData = graphData.monthly_values.map(item => {
    // Ensure values are numbers, handle string conversion
    const totalValue = typeof item.total_value === 'string' 
      ? parseFloat(item.total_value) || 0 
      : (item.total_value ?? 0)
    
    // Ensure we have valid numbers
    const numTotalValue = Number(totalValue)
    
    return {
      date: item.date,
      totalValue: isNaN(numTotalValue) ? 0 : numTotalValue,
    }
  })
  
  // Debug: log first few items to check data format
  if (process.env.NODE_ENV === 'development' && chartData.length > 0) {
    console.log('PortfolioChart data sample:', chartData.slice(0, 3))
    console.log('Raw graphData sample:', graphData.monthly_values.slice(0, 3))
  }
  
  const yMax = Math.max(
    ...chartData.map(item => item.totalValue),
    1
  )
  
  // Calculate appropriate upper bound based on actual data
  // Use 20% padding above the max value, rounded to nice intervals
  let yUpperBound: number
  if (yMax >= 1000000000) {
    // Billions: round to nearest 0.5B
    yUpperBound = Math.ceil((yMax * 1.2) / 500000000) * 500000000
  } else if (yMax >= 1000000) {
    // Millions: round to nearest 0.5M
    yUpperBound = Math.ceil((yMax * 1.2) / 500000) * 500000
  } else if (yMax >= 1000) {
    // Thousands: round to nearest 0.5K
    yUpperBound = Math.ceil((yMax * 1.2) / 500) * 500
  } else {
    // Small values: round to nearest 10
    yUpperBound = Math.ceil((yMax * 1.2) / 10) * 10
  }
  
  // Ensure minimum range for visibility
  if (yUpperBound < yMax * 1.1) {
    yUpperBound = yMax * 1.2
  }
  
  const yTicks = [0, yUpperBound * 0.25, yUpperBound * 0.5, yUpperBound * 0.75, yUpperBound]

  return (
    <Card variant="default" padding="none" className={cardClass}>
      <CardContent className="p-0">
        <div className={cn('w-full', mobile ? 'pl-[15px] pr-[6px] pb-[8px] pt-[8px]' : 'px-6 pb-[18px] pt-[24px]')} style={{ height: `${chartHeight}px` }}>
          <div className="w-full" style={{ height: `${plotHeight}px` }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={mobile ? { top: 34, right: 8, left: 4, bottom: 14 } : { top: 24, right: 8, left: 6, bottom: 14 }}>
                <defs>
                  <linearGradient id="portfolioTotalValueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={darkMode ? 'rgba(169,200,4,0.38)' : 'rgba(169,200,4,0.36)'} />
                    <stop offset="100%" stopColor="rgba(169,200,4,0.02)" />
                  </linearGradient>
                </defs>

                <CartesianGrid
                  vertical={true}
                  horizontal={true}
                  stroke={gridColor}
                  strokeDasharray="0"
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: mobile ? 11 : 12, fill: tickColor }}
                  tickMargin={mobile ? 8 : 10}
                  interval="preserveStartEnd"
                  axisLine={{ stroke: axisStroke }}
                  tickLine={false}
                  minTickGap={mobile ? 20 : 10}
                  tickFormatter={(value) => {
                    // Dates are already in "YYYY-MM" format, just return as-is
                    return value || ''
                  }}
                />
                <YAxis
                  ticks={yTicks}
                  domain={[0, yUpperBound]}
                  tickFormatter={formatYAxisMoney}
                  tick={{ fontSize: mobile ? 11 : 12, fill: tickColor }}
                  width={mobile ? 54 : 62}
                  axisLine={false}
                  tickLine={false}
                />

                <Tooltip
                  contentStyle={{
                    backgroundColor: darkMode ? '#080808' : '#ffffff',
                    border: darkMode ? '1px solid #171717' : '1px solid rgba(45,41,35,0.12)',
                    color: darkMode ? '#fdfdfd' : '#2d2923',
                    borderRadius: '0px',
                    boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                    padding: '10px 14px',
                  }}
                  labelStyle={{ color: darkMode ? 'rgba(253,253,253,0.72)' : 'rgba(45,41,35,0.72)' }}
                  formatter={(value: number, name: string) => {
                    if (name === 'totalValue') {
                      return [formatMoney(value), 'Total Value']
                    }
                    return [value, name]
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                />

                <Area
                  type="monotone"
                  dataKey="totalValue"
                  stroke="#a9c804"
                  fill="url(#portfolioTotalValueFill)"
                  fillOpacity={1}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: darkMode ? '#080808' : '#ffffff', stroke: '#a9c804', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className={cn('flex items-center justify-center', mobile ? 'mt-[4px] gap-5' : 'mt-[10px] gap-8')}>
            <div className="flex items-center gap-[8px]">
              <div className="h-[8px] w-[19px] bg-[#a9c804]" />
              <span className={cn("font-['Plus_Jakarta_Sans'] text-[12px] font-medium leading-[16px]", darkMode ? 'text-[#fdfdfd]' : 'text-[#2d2923]')}>
                Total Value
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default PortfolioChart
